'use client';

import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import AcademicProgramBadge from '@/app/Components/AcademicProgramBadge';

export type DegreeTarget = {
  ID: number;
  Name: string;
  'Degree Code'?: string | null;
};

type DegreeTargetsProps = {
  targets: DegreeTarget[];
  onView: (degree: DegreeTarget) => void;
  onRemove: (degree: DegreeTarget) => void;
  onAdd?: () => void;
  className?: string;
  /** Planner color per target, in the same order the planner assigns program colors. */
  colorFor?: (degree: DegreeTarget, index: number) => string;
};

/** Compact, responsive target-degree control shared by planner runtime and review fixtures. */
export default function DegreeTargets({
  targets,
  onView,
  onRemove,
  onAdd,
  className = '',
  colorFor,
}: DegreeTargetsProps) {
  return (
    <div className={`flex min-w-0 flex-1 flex-wrap items-center gap-1.5 ${className}`}>
      {targets.map((degree, index) => (
        <div key={degree.ID} className="group flex min-w-0 max-w-full items-center">
          <button
            type="button"
            onClick={() => onView(degree)}
            className="min-h-12 min-w-12 rounded-control text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] focus-visible:ring-offset-2"
            aria-label={`View ${degree.Name} degree information`}
          >
            <AcademicProgramBadge
              name={degree.Name}
              degreeCode={degree['Degree Code']}
              compact
              interactive={false}
              allowWrap
              accentColor={colorFor?.(degree, index)}
            />
          </button>
          <button
            type="button"
            onClick={() => onRemove(degree)}
            className="ml-1 inline-flex min-h-12 min-w-12 shrink-0 items-center justify-center rounded-control text-gray-400 opacity-100 transition hover:bg-red-50 hover:text-red-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] dark:hover:bg-red-950/20 md:h-10 md:w-10 md:min-h-10 md:min-w-10 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 md:focus:opacity-100 [@media(hover:none)]:opacity-100"
            title={`Remove ${degree.Name}`}
            aria-label={`Remove ${degree.Name}`}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      ))}

      {onAdd ? (
        <button
          type="button"
          onClick={onAdd}
          className="flex min-h-12 flex-shrink-0 items-center gap-1.5 rounded-full border border-dashed border-[var(--ss-border)] px-3 text-[var(--ss-text-muted)] transition hover:border-[var(--ss-brand)] hover:text-[var(--ss-brand)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] md:min-h-8"
        >
          <PlusIcon className="h-4 w-4" aria-hidden="true" />
          <span className="text-sm font-medium">Add program</span>
        </button>
      ) : null}
    </div>
  );
}
